/**
 * Invite a staff member, as a panel form. Keycloak emails the link; the
 * confirmation that it went stays a dialog, since it is the one moment the
 * operator learns the expiry, and closing it closes the form.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ApiError } from '@optum/api-client/client';
import {
  createPlatformStaffInvitation,
  type PlatformStaffInvitation,
} from '@optum/api-client/endpoints/platform-staff-invitations';
import type { PlatformStaffRole } from '@optum/api-client/types';

import {
  PanelFormActions,
  PanelFormFields,
  usePanelFormDone,
} from '../../components/shell/PanelForm.js';
import { Button } from '../../ui/Button.js';
import { Dialog, DialogActions } from '../../ui/Dialog.js';
import { Field } from '../../ui/Field.js';
import { Input, Typography } from '../../ui/finixt.js';
import { Select } from '../../ui/Select.js';
import { useToast } from '../../ui/useToast.js';
import { absoluteDateTime } from '../../lib/format.js';
import { qk } from '../../lib/query-keys.js';
import { clampTtl, readStoredTtl, TTL_MAX, TTL_MIN, writeStoredTtl } from './invite-ttl.js';

const ROLE_OPTIONS: { value: PlatformStaffRole; label: string }[] = [
  { value: 'optum-platform-viewer', label: 'optum-platform-viewer — reads everything' },
  { value: 'optum-platform-admin', label: 'optum-platform-admin — approves, rotates, suspends' },
];

export function InviteStaffForm() {
  const qc = useQueryClient();
  const toast = useToast();
  const done = usePanelFormDone();
  const [ttlMinutes, setTtlMinutes] = useState(() => readStoredTtl());
  const [role, setRole] = useState<PlatformStaffRole>('optum-platform-viewer');
  const [email, setEmail] = useState('');
  const [issued, setIssued] = useState<PlatformStaffInvitation | null>(null);

  const create = useMutation({
    mutationFn: () => {
      const ttl = clampTtl(ttlMinutes);
      // The expiry an operator picks is the one the next invitation starts from.
      writeStoredTtl(ttl);
      return createPlatformStaffInvitation({ role, email, ttl_minutes: ttl });
    },
    onSuccess: (out) => {
      setIssued(out);
      qc.invalidateQueries({ queryKey: qk.platformStaffInvitations() });
    },
    onError: (e) => toast.error('Invite failed', e instanceof ApiError ? e.message : String(e)),
  });

  return (
    <>
      <PanelFormFields onSubmit={() => create.mutate()}>
        <Typography variant="body-muted">
          Keycloak emails a link to verify the address and set a password. The invitee then signs in
          here.
        </Typography>
        <Field
          id="invite-email"
          label="Email"
          required
          hint="The invitee signs in with this address."
        >
          <Input
            id="invite-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="staff@optum.example"
            required
            autoFocus
          />
        </Field>
        <Field id="invite-role" label="Role" required>
          <Select
            id="invite-role"
            value={role}
            onChange={(value) => setRole(value as PlatformStaffRole)}
            options={ROLE_OPTIONS}
          />
        </Field>
        <Field
          id="invite-ttl"
          label="Expires in (minutes)"
          hint="One-time use. The value you choose becomes the default for the next invitation."
        >
          <Input
            id="invite-ttl"
            type="number"
            min={TTL_MIN}
            max={TTL_MAX}
            value={ttlMinutes}
            onChange={(e) => setTtlMinutes(Number(e.target.value))}
          />
        </Field>
        <PanelFormActions
          submitLabel="Send invitation"
          pending={create.isPending}
          disabled={!email}
          dirty={email !== '' && issued === null}
        />
      </PanelFormFields>
      <Dialog
        open={issued !== null}
        onClose={() => done()}
        title="Invitation sent"
        subtitle="Keycloak emailed the invitee. They set a password from the link, then sign in."
      >
        {issued ? (
          <div style={{ marginTop: 11, display: 'flex', flexDirection: 'column', gap: 7 }}>
            <div
              style={{
                borderRadius: 'var(--radius-chip)',
                background: 'var(--surface-sunken)',
                padding: 11,
                fontSize: 'var(--text-body-md)',
              }}
            >
              Email sent to <strong>{issued.invited_email}</strong>.
            </div>
            <Typography variant="meta">
              Role <span className="mono">{issued.invited_role}</span> · expires{' '}
              {absoluteDateTime(issued.expires_at)}. The invitee appears under Staff after their
              first sign-in.
            </Typography>
          </div>
        ) : null}
        <DialogActions>
          <Button variant="primary" onClick={() => done()}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
